import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { companyInfo } from '../data';
import { imageUrl } from '../lib/images';

export default function Varmepumper() {
  const location = useLocation();
  const isEn = location.pathname.startsWith('/en');

  const content = {
    da: {
      metaTitle: 'Varmepumper | Billund Varme & Sanitet',
      metaDesc:
        'Moderne og energieffektive varmepumper til din bolig. Vi hjælper dig med rådgivning, levering, installation og service i Billund og omegn.',
      canonical: 'https://billundvvs.netlify.app/varme/varmepumper/',
      h1: 'Varmepumper',
      h2: 'Moderne og energieffektive løsninger til din bolig',
      heroLead:
        'Vi hjælper dig med rådgivning, levering, installation og service af varmepumper. En varmepumpe er en effektiv og klimavenlig varmeløsning, der kan reducere dine varmeudgifter og give et bedre indeklima.',
      heroCta: 'Få et uforpligtende tilbud',
      benefits: [
        {
          icon: 'fa-solid fa-piggy-bank',
          title: 'Lavere varmeregning',
          desc: 'En varmepumpe kan reducere dine varmeudgifter markant.',
        },
        {
          icon: 'fa-solid fa-leaf',
          title: 'Miljøvenlig løsning',
          desc: 'Udnytter energi fra luften og reducerer dit CO₂-aftryk.',
        },
        {
          icon: 'fa-solid fa-temperature-half',
          title: 'Komfort hele året',
          desc: 'Behagelig varme om vinteren og køling om sommeren.',
        },
        {
          icon: 'fa-solid fa-screwdriver-wrench',
          title: 'Service og vedligehold',
          desc: 'Vi sørger for løbende service, så din varmepumpe fungerer optimalt.',
        },
      ],
      galleryTitle: 'Eksempler på varmepumpeinstallationer',
      ctaButton: 'Få et uforpligtende tilbud',
      gallery: [
        {
          image: '/images/utility-room.webp',
          alt: 'Ældre varmeanlæg',
          tag: 'FØR',
          tagClass: 'bg-[#5b6471]',
          caption: 'Gammel oliekedel',
        },
        {
          image: '/images/utility-room-heating.webp',
          alt: 'Ny varmeinstallation',
          tag: 'EFTER',
          tagClass: 'bg-[#d11218]',
          caption: 'Ny varmepumpe med indendørs unit',
        },
        {
          image: 'https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/dagslys-ved-lagerbygningen-1-varmepumpe.png',
          alt: 'Moderne luft til vand varmepumpe ved lagerbygning',
          tag: null,
          caption: 'Udendørs unit (luft til vand varmepumpe)',
        },
        {
          image: '/images/vvs-pipes-technical.webp',
          alt: 'Rørinstallation',
          tag: null,
          caption: 'Indendørs unit og rørsystem',
        },
      ],
      typesTitle: 'Vi tilbyder forskellige typer varmepumper',
      types: [
        {
          image: 'https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/dagslys-ved-lagerbygningen-1-varmepumpe.png',
          alt: 'Luft til vand varmepumpe installeret af Billund Varme & Sanitet',
          title: 'Luft til vand varmepumpe',
          desc: 'Velegnet til både nye og eksisterende boliger. Kan erstatte olie- eller gasfyr.',
        },
        {
          image: '/images/heat-pump.webp',
          alt: 'Luft til luft varmepumpe',
          title: 'Luft til luft varmepumpe',
          desc: 'Effektiv opvarmning og mulighed for køling om sommeren.',
        },
        {
          image: '/images/utility-room-heating.webp',
          alt: 'Jordvarmeanlæg',
          title: 'Jordvarme (væske til vand)',
          desc: 'En stabil og meget energieffektiv løsning med lavt energiforbrug.',
        },
      ],
      bottomTitle: 'Få professionel rådgivning om varmepumper',
      bottomText:
        'Vi hjælper dig med at finde den rigtige løsning, der passer til din bolig, dit behov og dit budget – og vi står for hele installationen fra start til slut.',
      bottomCta: 'Kontakt os i dag',
    },
    en: {
      metaTitle: 'Heat Pumps | Billund Varme & Sanitet',
      metaDesc:
        'Modern, energy-efficient heat pump solutions. Consultation, delivery, installation and service in Billund and surrounding areas.',
      canonical: 'https://billundvvs.netlify.app/en/heating/heat-pumps/',
      h1: 'Heat Pumps',
      h2: 'Modern and energy-efficient heating solutions for your home',
      heroLead:
        'We help you with advice, delivery, certified installation and service of heat pumps. A heat pump is an efficient and eco-friendly heating solution that reduces running costs and improves indoor climate.',
      heroCta: 'Get a non-binding quote',
      benefits: [
        {
          icon: 'fa-solid fa-piggy-bank',
          title: 'Lower heating bills',
          desc: 'A heat pump can significantly reduce your annual energy bills.',
        },
        {
          icon: 'fa-solid fa-leaf',
          title: 'Eco-friendly solution',
          desc: 'Harnesses natural air energy and cuts your carbon footprint.',
        },
        {
          icon: 'fa-solid fa-temperature-half',
          title: 'Year-round comfort',
          desc: 'Pleasant warmth during winter and active cooling in summer.',
        },
        {
          icon: 'fa-solid fa-screwdriver-wrench',
          title: 'Service and maintenance',
          desc: 'We provide annual service to ensure your pump operates at peak efficiency.',
        },
      ],
      galleryTitle: 'Examples of heat pump installations',
      ctaButton: 'Get a non-binding quote',
      gallery: [
        {
          image: '/images/utility-room.webp',
          alt: 'Older heating system',
          tag: 'BEFORE',
          tagClass: 'bg-[#5b6471]',
          caption: 'Old oil boiler',
        },
        {
          image: '/images/utility-room-heating.webp',
          alt: 'New heating installation',
          tag: 'AFTER',
          tagClass: 'bg-[#d11218]',
          caption: 'New heat pump with indoor unit',
        },
        {
          image: 'https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/dagslys-ved-lagerbygningen-1-varmepumpe.png',
          alt: 'Modern air-to-water heat pump outside facility',
          tag: null,
          caption: 'Outdoor unit (air-to-water heat pump)',
        },
        {
          image: '/images/vvs-pipes-technical.webp',
          alt: 'Pipework and indoor connection',
          tag: null,
          caption: 'Indoor unit and pipework system',
        },
      ],
      typesTitle: 'We provide different types of heat pumps',
      types: [
        {
          image: 'https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/dagslys-ved-lagerbygningen-1-varmepumpe.png',
          alt: 'Air-to-water heat pump installed by Billund Varme & Sanitet',
          title: 'Air-to-water heat pump',
          desc: 'Ideal for both newly built and existing homes. Replaces old oil or gas boilers.',
        },
        {
          image: '/images/heat-pump.webp',
          alt: 'Air-to-air heat pump',
          title: 'Air-to-air heat pump',
          desc: 'Efficient heating with cooling functionality during hot summer months.',
        },
        {
          image: '/images/utility-room-heating.webp',
          alt: 'Ground source geothermal heating',
          title: 'Ground source (Geothermal)',
          desc: 'A reliable, highly efficient heating solution with minimal operating costs.',
        },
      ],
      bottomTitle: 'Get professional heat pump guidance',
      bottomText:
        'We help you select the ideal heat pump configuration matching your house, heating demand, and budget – managing the entire installation end-to-end.',
      bottomCta: 'Contact us today',
    },
  };

  const t = isEn ? content.en : content.da;

  return (
    <div className="font-sans text-[#06243d] bg-white selection:bg-[#df0011] selection:text-white">
      <Helmet>
        <title>{t.metaTitle}</title>
        <meta name="description" content={t.metaDesc} />
        <meta property="og:title" content={t.metaTitle} />
        <meta property="og:description" content={t.metaDesc} />
        <link rel="canonical" href={t.canonical} />
      </Helmet>

      {/* SECTION 1: HERO with right-side cover photo and soft gradient flow */}
      <section className="relative overflow-hidden bg-white border-b border-gray-100 py-10 sm:py-16">
        {/* Desktop right-side cover image with smooth gradient flow */}
        <div className="hidden lg:block absolute inset-y-0 right-0 w-1/2 xl:w-[50%] 2xl:w-[52%] h-full overflow-hidden pointer-events-none">
          {/* Gentle edge gradient transition from white text container into image */}
          <div className="absolute inset-y-0 left-0 w-44 bg-gradient-to-r from-white via-white/80 to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-white/20 to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white/20 to-transparent z-10 pointer-events-none" />

          <img
            src="https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/dagslys-ved-lagerbygningen-1-varmepumpe.png"
            alt="Moderne luft til vand varmepumpe ved Billund Varme & Sanitet A/S"
            className="w-full h-full object-cover object-center"
            loading="eager"
          />

          {/* TEKNIQ badge overlay */}
          <div className="absolute bottom-5 right-6 z-20 hidden xl:flex items-center gap-2 bg-white/95 backdrop-blur-xs px-3.5 py-1.5 rounded-md shadow-md border border-slate-200/80">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[11px] font-bold text-[#06243d] tracking-wide uppercase">
              Autoriseret installatør • TEKNIQ Garanti
            </span>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Left Column: Heading, Subheading & CTAs */}
            <div className="lg:col-span-6 xl:col-span-6">
              <h1 className="text-4xl sm:text-5xl lg:text-[3.25rem] font-black text-[#06243d] tracking-tight leading-[1.08] mb-2">
                {t.h1}
              </h1>
              <p className="text-xl sm:text-2xl font-bold text-[#06243d] mb-4">
                {t.h2}
              </p>
              <p className="text-slate-700 text-base leading-relaxed mb-6 max-w-xl">
                {t.heroLead}
              </p>

              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to={isEn ? '/en/get-a-quote' : '/tilbud'}
                  className="inline-flex items-center gap-2 bg-[#df0011] hover:bg-[#b7000d] text-white font-bold px-6 sm:px-7 py-3 sm:py-3.5 rounded-md transition-all shadow-sm hover:shadow"
                >
                  <span>{t.heroCta}</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>

                <a
                  href={`tel:+45${companyInfo.phone.replace(/\s+/g, '')}`}
                  className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-[#06243d] border border-slate-300 font-bold px-5 sm:px-6 py-3 sm:py-3.5 rounded-md transition-colors"
                >
                  <i className="fa-solid fa-phone text-[#df0011]" aria-hidden="true" />
                  <span>{companyInfo.phone}</span>
                </a>
              </div>

              {/* Mobile / Tablet Image with soft gradient transitions */}
              <div className="mt-8 lg:hidden relative w-full aspect-[16/10] sm:aspect-[16/9] rounded-xl overflow-hidden shadow-sm border border-slate-200/80 bg-white">
                <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white via-white/80 to-transparent z-10 pointer-events-none" />
                <div className="absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-white/50 to-transparent z-10 pointer-events-none" />
                <img
                  src="https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/dagslys-ved-lagerbygningen-1-varmepumpe.png"
                  alt="Moderne luft til vand varmepumpe ved Billund Varme & Sanitet A/S"
                  className="w-full h-full object-cover object-center"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: BENEFITS (4-column row with circular symbols and vertical dividers) */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-8 lg:px-12 py-12 md:py-16">
        <div className="grid grid-cols-4 max-md:grid-cols-2 max-sm:grid-cols-1 gap-6 sm:gap-8">
          {t.benefits.map((b, idx) => (
            <article
              key={idx}
              className={`flex gap-4 items-start ${
                idx !== t.benefits.length - 1 ? 'md:border-r md:border-[#d4e0e8] md:pr-6' : ''
              } ${idx === 1 ? 'max-md:border-r-0' : ''}`}
            >
              <span className="shrink-0 grid place-items-center bg-[#06243d] text-white w-[54px] h-[54px] rounded-full text-xl select-none shadow-sm">
                <i className={`${b.icon || 'fa-solid fa-check'} text-xl`} aria-hidden="true" />
              </span>
              <div>
                <h3 className="text-[15px] font-[700] m-0 mb-1.5 text-[#06243d] leading-snug">
                  {b.title}
                </h3>
                <p className="text-[13px] m-0 text-slate-600 leading-[1.55]">
                  {b.desc}
                </p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* SECTION 3: EKSEMPLER PÅ VARMEPUMPEINSTALLATIONER (Matches Fjernvarme showcase grid exactly) */}
      <section className="bg-[#edf2f7] py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#09263f] mb-8 sm:mb-10">
            {t.galleryTitle}
          </h2>

          {/* 4 Columns Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
            {t.gallery.map((item, idx) => (
              <div key={idx} className="flex flex-col">
                {/* Image Container with high visual fidelity and hover zoom */}
                <div className="overflow-hidden rounded-lg shadow-sm border border-gray-200/80 bg-white aspect-[3/4] sm:aspect-[4/5] group">
                  <img
                    src={imageUrl(item.image, 'width=700,quality=85,format=auto')}
                    alt={item.alt}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>

                {/* Badge if present (FØR / EFTER) */}
                {item.tag && (
                  <div
                    className={`w-full ${item.tagClass} text-white text-center font-bold uppercase tracking-wider py-2 rounded-md text-sm mt-3 shadow-xs`}
                  >
                    {item.tag}
                  </div>
                )}

                {/* Caption Text */}
                <p
                  className={`text-center font-medium text-gray-800 text-sm sm:text-base leading-snug ${
                    item.tag ? 'mt-2' : 'mt-3'
                  }`}
                >
                  {item.caption}
                </p>
              </div>
            ))}
          </div>

          {/* Centered Primary CTA Button */}
          <div className="mt-10 sm:mt-12 text-center">
            <Link
              to={isEn ? '/en/get-a-quote' : '/tilbud'}
              className="inline-flex items-center justify-center gap-2.5 px-8 py-3.5 bg-[#d11218] hover:bg-[#b50e14] text-white font-semibold text-base sm:text-lg rounded-md shadow-sm transition-all transform hover:-translate-y-0.5"
            >
              <span>{t.ctaButton}</span>
              <i className="fa-solid fa-arrow-right text-sm"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 4: TYPER AF VARMEPUMPER (3-column grid) */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-8 lg:px-12 py-14 md:py-20">
        <div className="mb-8">
          <h2 className="text-[clamp(24px,2.8vw,32px)] tracking-[-0.03em] m-0 font-[800] text-[#06243d]">
            {t.typesTitle}
          </h2>
        </div>
        <div className="grid grid-cols-3 max-md:grid-cols-1 gap-6 md:gap-8">
          {t.types.map((type, idx) => (
            <article key={idx} className="group bg-slate-50/50 p-3 rounded-xl border border-slate-200/60 transition-all hover:border-slate-300">
              <img
                src={type.image}
                alt={type.alt}
                className="w-full h-[190px] max-md:h-[220px] object-cover rounded-lg shadow-xs"
              />
              <div className="pt-4 px-1">
                <h3 className="text-[17px] font-[700] m-0 mb-2 text-[#06243d]">
                  {type.title}
                </h3>
                <p className="text-[14px] m-0 text-slate-600 leading-[1.6]">
                  {type.desc}
                </p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* SECTION 5: AFSLUTTENDE HERO / BOTTOM BANNER (Deep navy overlay gradient - opacity tæt på 0 i højre side) */}
      <section
        className="relative text-white py-16 md:py-20 bg-cover bg-center overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(90deg, #06243d 0%, rgba(6, 36, 61, 0.95) 35%, rgba(6, 36, 61, 0.4) 55%, rgba(6, 36, 61, 0) 80%), url('/images/utility-room-heating.webp')`,
          backgroundPosition: 'center center',
        }}
      >
        <div className="w-full max-w-[1200px] mx-auto px-6 sm:px-8 lg:px-12 relative z-10 flex items-center">
          <div className="max-w-[520px]">
            <h2 className="text-[clamp(26px,3vw,34px)] font-[800] leading-[1.2] m-0 mb-3 text-white">
              {t.bottomTitle}
            </h2>
            <p className="text-[15px] leading-[1.6] m-0 mb-6 text-slate-200">
              {t.bottomText}
            </p>
            <Link
              to={isEn ? '/en/contact' : '/kontakt'}
              className="inline-flex items-center justify-center gap-4 bg-[#df0011] hover:bg-[#b7000d] text-white px-8 py-3.5 rounded-lg font-[700] text-[16px] transition-all duration-200 shadow-md hover:shadow-lg active:scale-[0.99]"
            >
              <span>{t.bottomCta}</span>
              <span className="text-[19px] leading-none">→</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
